import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useCart } from '../context/CartContext';
import axios from 'axios';
import { Tag, Clock, Sparkles, ArrowRight, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const useCountdown = (targetIso) => {
  const [remaining, setRemaining] = useState(null);
  useEffect(() => {
    if (!targetIso) { setRemaining(null); return; }
    const tick = () => {
      const target = new Date(targetIso).getTime();
      const diff = target - Date.now();
      if (diff <= 0) { setRemaining({ expired: true }); return; }
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setRemaining({ days, hours, minutes, seconds, expired: false });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetIso]);
  return remaining;
};

const PromoLanding = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { applyCoupon } = useCart();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [applying, setApplying] = useState(false);
  const countdown = useCountdown(data?.valid_until);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/coupons/public/${encodeURIComponent(code)}`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.status === 404 ? 'not_found' : 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [code]);

  const handleApply = async () => {
    setApplying(true);
    const res = await applyCoupon(code);
    setApplying(false);
    if (res.ok) {
      if (data?.product?.id) {
        navigate(`/product/${data.product.id}`);
      } else {
        navigate('/catalog');
      }
    }
  };

  const productName = (p) => {
    if (!p?.name) return '';
    return p.name[i18n.language] || p.name.sk || p.name.en || Object.values(p.name)[0] || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 flex items-center justify-center">
        <div className="animate-spin h-10 w-10 border-4 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md text-center bg-white rounded-2xl border border-gray-200 p-8" data-testid="promo-not-found">
          <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">{t('promo.not_found_title')}</h1>
          <p className="text-gray-600 mb-6">{t('promo.not_found_body')}</p>
          <Link to="/catalog" className="inline-block px-5 py-2.5 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition">
            {t('promo.go_to_catalog')}
          </Link>
        </div>
      </div>
    );
  }

  const isUsable = data.status === 'active' && !countdown?.expired;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-orange-900 to-amber-900 text-white overflow-hidden relative">
      <Helmet>
        <title>{`−${data.discount_percent}% ${data.code} · Sunify`}</title>
        <meta name="description" content={t('promo.subtitle')} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`−${data.discount_percent}% · ${data.code} · Sunify`} />
        <meta property="og:description" content={t('promo.subtitle')} />
        <meta property="og:image" content={`${BACKEND_URL}/api/og/promo/${encodeURIComponent(data.code)}.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`−${data.discount_percent}% · ${data.code} · Sunify`} />
        <meta name="twitter:description" content={t('promo.subtitle')} />
        <meta name="twitter:image" content={`${BACKEND_URL}/api/og/promo/${encodeURIComponent(data.code)}.png`} />
      </Helmet>
      {/* Decorative glow */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(251,191,36,0.25),transparent_70%)]" />
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-orange-500 rounded-full blur-3xl opacity-30" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-amber-400 rounded-full blur-3xl opacity-20" />

      <div className="relative container mx-auto px-4 py-16 lg:py-24">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-center gap-2 mb-6 text-orange-300">
            <Sparkles className="h-5 w-5" />
            <span className="text-sm font-semibold uppercase tracking-[0.2em]">{t('promo.eyebrow')}</span>
            <Sparkles className="h-5 w-5" />
          </div>

          <h1 className="text-center text-5xl md:text-7xl font-black mb-4 leading-none" data-testid="promo-title">
            <span className="bg-gradient-to-r from-orange-300 via-amber-200 to-yellow-300 bg-clip-text text-transparent">
              −{data.discount_percent}%
            </span>
          </h1>
          <p className="text-center text-xl md:text-2xl text-orange-100 mb-10">
            {t('promo.subtitle')}
          </p>

          {/* Code card */}
          <div className="bg-white/10 backdrop-blur-xl border-2 border-dashed border-orange-300/60 rounded-3xl p-6 md:p-10 mb-8 shadow-2xl">
            <div className="flex items-center justify-center gap-2 mb-3 text-orange-200">
              <Tag className="h-4 w-4" />
              <span className="text-xs uppercase tracking-[0.2em] font-semibold">{t('promo.your_code')}</span>
            </div>
            <div
              className="text-center font-mono text-3xl md:text-5xl font-extrabold tracking-[0.2em] md:tracking-[0.35em] text-yellow-300 bg-gray-900/50 rounded-2xl py-5 md:py-7 mb-6 select-all"
              data-testid="promo-code"
            >
              {data.code}
            </div>

            {/* Countdown */}
            {countdown && !countdown.expired && (
              <div className="mb-6">
                <div className="flex items-center justify-center gap-2 mb-3 text-orange-200">
                  <Clock className="h-4 w-4" />
                  <span className="text-xs uppercase tracking-[0.2em] font-semibold">{t('promo.expires_in')}</span>
                </div>
                <div className="grid grid-cols-4 gap-2 md:gap-3" data-testid="promo-countdown">
                  <CountCell value={countdown.days} label={t('promo.days')} />
                  <CountCell value={countdown.hours} label={t('promo.hours')} />
                  <CountCell value={countdown.minutes} label={t('promo.minutes')} />
                  <CountCell value={countdown.seconds} label={t('promo.seconds')} />
                </div>
              </div>
            )}

            {data.status === 'used' && (
              <p className="text-center text-red-300 font-semibold mb-2" data-testid="promo-used">
                {t('promo.status_used')}
              </p>
            )}
            {(data.status === 'expired' || countdown?.expired) && (
              <p className="text-center text-red-300 font-semibold mb-2" data-testid="promo-expired">
                {t('promo.status_expired')}
              </p>
            )}

            <button
              onClick={handleApply}
              disabled={!isUsable || applying}
              className="w-full py-4 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-2xl font-bold text-lg shadow-xl transition flex items-center justify-center gap-2 disabled:from-gray-500 disabled:to-gray-600 disabled:cursor-not-allowed"
              data-testid="apply-and-shop"
            >
              {applying ? '…' : t('promo.cta_apply')}
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>

          {/* Product teaser */}
          {data.product && (
            <div className="bg-white/5 backdrop-blur rounded-2xl p-4 md:p-6 border border-white/10">
              <div className="flex items-center gap-4">
                {data.product.images?.[0] ? (
                  <img
                    src={data.product.images[0]}
                    alt={productName(data.product)}
                    className="w-20 h-20 md:w-24 md:h-24 object-cover rounded-xl border border-white/20"
                  />
                ) : (
                  <div className="w-20 h-20 md:w-24 md:h-24 bg-white/10 rounded-xl flex items-center justify-center">
                    <Tag className="h-8 w-8 text-orange-300" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-orange-200 uppercase tracking-widest mb-1">{t('promo.applies_to')}</p>
                  <h3 className="text-lg md:text-xl font-bold truncate">{productName(data.product)}</h3>
                  {data.product.sku && (
                    <p className="text-xs text-white/50 font-mono mt-1">{data.product.sku}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="text-center mt-8">
            <Link to="/catalog" className="text-sm text-orange-200 hover:text-white transition">
              {t('promo.or_browse')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const CountCell = ({ value, label }) => (
  <div className="bg-white/10 backdrop-blur rounded-xl py-3 px-2 text-center border border-white/10">
    <div className="text-2xl md:text-4xl font-bold text-white tabular-nums">
      {String(value).padStart(2, '0')}
    </div>
    <div className="text-[10px] md:text-xs uppercase tracking-widest text-orange-200 mt-0.5">{label}</div>
  </div>
);

export default PromoLanding;
